import { Button, useTranslation } from '@hbs-us/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useId, useState } from 'react';

import { apiFetch } from '../../../lib/apiFetch.js';
import { openCredentialed } from '../../../lib/download.js';
import { blueprintFileUrl } from '../fileLinks.js';
import { useJobBlueprintsQuery } from '../useJobQuery.js';

/**
 * The Blueprints tab — upload a blueprint to the job + the list of existing blueprints.
 *
 * The upload POSTs a `multipart/form-data` body to `POST /api/v1/jobs/:id/blueprints` (the server
 * stores the bytes to R2 under the `{tenantId}/` key prefix and holds the Admin/Manager gate). The
 * upload rides `apiFetch` so the double-submit CSRF header is attached on the non-safe method; the
 * SPA never sends a tenant id (SEC-11). On success the blueprint list re-fetches. Every color is a
 * locked `@hbs-us/ui` token (no hex).
 */

export function BlueprintsTab({ jobId }: { jobId: number }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useJobBlueprintsQuery(jobId);

  const titleId = useId();
  const fileId = useId();
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation<{ ok: boolean }, Error, FormData>({
    mutationFn: async (formData) => {
      const response = await apiFetch(`/api/v1/jobs/${jobId}/blueprints`, {
        method: 'POST',
        body: formData,
      });
      const body = (await response.json().catch(() => null)) as { ok?: boolean } | null;
      if (!response.ok || !body || body.ok === false) {
        throw new Error('upload_failed');
      }
      return { ok: true };
    },
    onSuccess: () => {
      setTitle('');
      setFile(null);
      void queryClient.invalidateQueries({ queryKey: ['job', jobId, 'blueprints'] });
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!file) return;
    const formData = new FormData();
    formData.set('title', title);
    formData.set('document', file);
    upload.mutate(formData);
  }

  const blueprints = query.data?.blueprints ?? [];

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="rounded-md border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold text-foreground text-lg">
          {t('jobs:blueprints.uploadHeading')}
        </h3>
        {upload.isError ? (
          <p role="alert" className="mb-3 text-destructive text-sm">
            {t('jobs:blueprints.uploadError')}
          </p>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={titleId} className="font-medium text-foreground text-sm">
              {t('jobs:blueprints.title')}
            </label>
            <input
              id={titleId}
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={fileId} className="font-medium text-foreground text-sm">
              {t('jobs:blueprints.file')}
            </label>
            <input
              id={fileId}
              type="file"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button type="submit" disabled={upload.isPending || !file}>
            {t('jobs:blueprints.upload')}
          </Button>
        </div>
      </form>

      <div className="rounded-md border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold text-foreground text-lg">
          {t('jobs:blueprints.listHeading')}
        </h3>
        {query.isPending ? (
          <p className="text-muted-foreground text-sm">{t('common:status.loading')}</p>
        ) : blueprints.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('jobs:blueprints.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {blueprints.map((bp) => (
              <li
                key={bp.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <span className="font-medium text-foreground">{bp.title}</span>
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground text-xs">
                    {bp.originalFilename ?? t('jobs:blueprints.noFile')}
                  </span>
                  {bp.originalFilename ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        void openCredentialed(blueprintFileUrl(jobId, bp.id)).catch((err) =>
                          console.error('blueprint view failed', err),
                        )
                      }
                    >
                      {t('jobs:blueprints.view')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
